import React, { ReactNode } from 'react';

const CodeBlock = ({ code, language, position }: { code: ReactNode, language: string, position: string }) => {
  return (
    <div className="w-[400px] pointer-events-none select-none">
      <div className="bg-black/10 backdrop-blur-sm rounded-lg p-4 border border-white/5">
      <pre className="font-mono text-sm">
        <code>
          {code}
        </code>
      </pre>
      </div>
    </div>
  );
};

const ServicesBackground: React.FC = () => {
  const codeSnippets = {
    topLeft: `
// Service Factory
function createService(config) {
  return {
    name: config.name,
    type: config.type,
    features: config.features || [],
    status: 'initialized',
    
    init() {
      this.status = 'active';
      console.log(\`Service \${this.name} started\`);
    },
    
    addFeature(feature) {
      this.features.push(feature);
    }
  };
}`,
    topRight: `
interface ServiceConfig {
  name: string;
  type: 'frontend' | 'backend';
  features?: string[];
  metadata?: {
    version: string;
    author: string;
    created: Date;
  };
}

type ServiceStatus = 
  | 'initialized'
  | 'active'
  | 'paused'
  | 'terminated';

interface ServiceInstance {
  id: string;
  config: ServiceConfig;
  status: ServiceStatus;
}`,
    bottomLeft: `
/* Service Card Styles */
.service-card {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 2rem;
  transition: all 0.3s ease;
}

.service-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.08);
}

.service-icon {
  position: relative;
  z-index: 1;
  animation: pulse 2s infinite;
}`
  };

  const formatCode = (code: string) => {
    return code.split('\n').map((line, index) => {
      // Comments
      if (line.trim().startsWith('//') || line.trim().startsWith('/*')) {
        return <span key={index} className="block text-gray-400">{line}</span>;
      }
      
      // Keywords
      line = line.replace(/(function|interface|type|const|return|this|class|private|public|constructor|new|if|throw)/g, 
        '<span class="text-yellow-400">$1</span>');
      
      // Types and classes
      line = line.replace(/(ServiceConfig|ServiceStatus|ServiceInstance|Service|Config|Instance)/g, 
        '<span class="text-blue-400">$1</span>');
      
      // Function names
      line = line.replace(/(createService|init|addFeature)/g, 
        '<span class="text-green-400">$1</span>');
      
      // CSS properties and values
      line = line.replace(/(backdrop-filter|background|border|border-radius|padding|transition|transform|box-shadow|position|z-index|animation)/g, 
        '<span class="text-pink-400">$1</span>');

      return (
        <span 
          key={index} 
          className="block text-gray-200"
          dangerouslySetInnerHTML={{ __html: line }}
        />
      );
    });
  };

  return (
    <>
      {/* Top Section */}
      <div className="absolute top-0 w-full flex justify-between opacity-[0.15]">
        <div className="-ml-20 mt-32">
          <CodeBlock 
            code={formatCode(codeSnippets.topLeft)} 
            language="javascript"
            position="topLeft"
          />
        </div>
        <div className="-mr-20 mt-48">
          <CodeBlock 
            code={formatCode(codeSnippets.topRight)} 
            language="typescript"
            position="topRight"
          />
        </div>
      </div>

      {/* Bottom Section */}
      <div className="absolute bottom-32 w-full flex justify-between opacity-[0.15]">
        <div className="-ml-20">
          <CodeBlock 
            code={formatCode(codeSnippets.bottomLeft)} 
            language="css"
            position="bottomLeft"
          />
        </div>
      </div>
    </>
  );
};

export default ServicesBackground; 